import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Button,
	HStack,
	Heading,
	Skeleton,
	Table,
	Tbody,
	Td,
	Th,
	Thead,
	Tooltip,
	Tr
} from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { HiChevronLeft } from "react-icons/hi";
import { Link, Location, generatePath, useLocation, useNavigate, useParams } from "react-router-dom";

// Components
import DataCardDisplay, { DataItem } from "../../../shared/DataDisplay/DataDisplay";
import LinkableTableCell from "../../../shared/DataTable/shared/LinkableTableCell";
import LoadingSpinner from "../../../shared/LoadingSpinner/LoadingSpinner";
import SectionHeader from "../../../shared/SectionHeader/SectionHeader";
import LayoutWrapper from "../../../shared/layout";
import { Page } from "../../../shared/layout/index.styles";

// Providers
import { useAttorneyProvider } from "../../../context-providers/attorney-provider";

// Protos
import { CaseClaimDetail } from "../../../utils/api/protos/messages/claims.message_pb";

// Utils
import { RouterPathMapping } from "../../../Router";
import { ClaimStatus } from "../../../utils/consts";
import {
	convertUnixTimestampToLocalDate,
	getClaimStatusFromBffStatus,
	processClaimStatusForAttorney
} from "../../../utils/helpers";

interface CaseDetailProps {}

const AttorneyCaseDetail: FunctionComponent<CaseDetailProps> = (): ReactElement => {
	const { id } = useParams();
	const { claimCase, claimList, handleFetchClaimDetails, handleFetchLampCase, handleFetchHasPolicy } =
		useAttorneyProvider();
	const navigate = useNavigate();
	const { state } = useLocation() as Location<{ from: string }>;
	const [isInitialLoading, setInitialLoading] = useState(true);
	const [hasPolicy, setHasPolicy] = useState<boolean | undefined>(undefined);

	const renderClaimServices = (claim: CaseClaimDetail): ReactElement => {
		const serviceDetails = claim.getServiceDetailsList();
		const highValueServices = new Set(serviceDetails.map((serviceDetail) => serviceDetail.getService()));
		const standardServices = claim.getServiceList().filter((service) => !highValueServices.has(service));

		return (
			<Box>
				{standardServices.join(", ")}
				{serviceDetails.length > 0 && (
					<Accordion allowToggle>
						{serviceDetails.map((serviceDetail, index) => (
							<AccordionItem key={`${serviceDetail.getService()}-${index}`}>
								<AccordionButton px={0}>
									<Box as="span" flex="1" textAlign="left">
										{serviceDetail.getService()}
									</Box>
									<AccordionIcon />
								</AccordionButton>
								<AccordionPanel px={0}>
									{serviceDetail.getCaseWorkLevelsList().join(", ") || "No service levels selected"}
								</AccordionPanel>
							</AccordionItem>
						))}
					</Accordion>
				)}
			</Box>
		);
	};

	const buildCaseInformation = (): ReactElement => {
		const caseData: Array<DataItem> = [
			{
				title: "PASS Case ID",
				value: `${claimCase?.getCaseid() || ""}`,
				isurl: false
			},
			{
				title: "LAMP Case ID",
				value: `${claimCase?.getLampcaseid() || ""}`,
				isurl: false
			},
			{
				title: "Legal Matter Location",
				value: `${claimCase?.getProvidercity() || ""}, ${claimCase?.getProviderstate() || ""}, ${claimCase?.getProviderzip() || ""}`,
				isurl: false
			}
		];

		if (claimCase?.hasLampcaseid()) {
			caseData.push({
				title: "Legal Matter County",
				value: `${claimCase?.getLegalmattercounty() || ""}`,
				isurl: false
			});
			caseData.push({
				title: "Area of Law",
				value: `${claimCase.getAreaoflaw() || ""}`,
				isurl: false
			});
		}

		caseData.push({
			title: "Submitted On",
			value: claimCase?.getIntakedate()
				? convertUnixTimestampToLocalDate(claimCase.getIntakedate()).toLocaleDateString("en-US")
				: "",
			isurl: false
		});
		caseData.push({
			title: "Accepted On",
			value: claimCase?.getReferralaccepteddate()
				? convertUnixTimestampToLocalDate(claimCase.getReferralaccepteddate()).toLocaleDateString("en-US")
				: "",
			isurl: false
		});

		return (
			<DataCardDisplay
				leftSideRows={claimCase?.hasLampcaseid() ? 3 : 2}
				rightSideRows={claimCase?.hasLampcaseid() ? 3 : 2}
				data={caseData}
				style={{ width: "100%" }}
				leftLabelMinWidth={110}
				rightLabelMinWidth={180}
			/>
		);
	};

	useEffect(() => {
		if (id) {
			const parsedId = parseInt(id);
			handleFetchLampCase(parsedId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		if (claimCase) {
			handleFetchClaimDetails().finally(() => setInitialLoading(false));
			handleFetchHasPolicy().then((hasPolicy) => setHasPolicy(hasPolicy));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claimCase]);

	return (
		<LayoutWrapper>
			<HStack height="100%" overflow="hidden" gap="0">
				{isInitialLoading && <LoadingSpinner />}
				<Box width="100%" height="100%" overflow="auto">
					<Box mx={8}>
						<Page>
							{/* Header Section */}
							<HStack alignItems="center" width="99%" marginBottom="1%">
								<Button
									leftIcon={<HiChevronLeft />}
									variant="ghost"
									onClick={() => navigate(state?.from ?? RouterPathMapping.AttorneyCases)}
									mr="80px">
									Back
								</Button>
								<Heading fontSize="21px" fontWeight="700" color="gray.700" flex={1} textAlign="center">
									Case {id ? "#" + id : ""}:&nbsp;{claimCase?.getProvidername() || ""}
								</Heading>
								<Tooltip
									label={
										claimCase?.getMemberid() === 0 || claimCase?.getMemberid() == null || !hasPolicy
											? "Claim creation is blocked because this member does not have paid benefits."
											: ""
									}>
									<Button
										colorScheme="orange"
										as={Link}
										isDisabled={
											claimCase?.getMemberid() === 0 ||
											claimCase?.getMemberid() == null ||
											!hasPolicy ||
											hasPolicy === undefined
										}
										to={
											claimCase?.getMemberid() !== 0 && claimCase?.getMemberid() != null && hasPolicy
												? generatePath(RouterPathMapping.Attorney_ClaimCreate, {
														caseId: claimCase?.getCaseid().toString() || ""
													})
												: ""
										}>
										Create Claim
									</Button>
								</Tooltip>
							</HStack>
							<Box>
								<SectionHeader title="Case Information" width="100%" />
								<Skeleton isLoaded={!!claimCase} endColor="gray.200">
									{buildCaseInformation()}
								</Skeleton>
							</Box>
							<Box>
								<SectionHeader title="Member Information" width="100%" />
								<Skeleton isLoaded={!!claimCase} endColor="gray.200">
									<DataCardDisplay
										leftSideRows={2}
										rightSideRows={1}
										data={[
											{
												title: "Member ID",
												value: `${claimCase?.getMemberid() || ""}`,
												isurl: false
											},
											{
												title: "Member Name",
												value: `${claimCase?.getClientnamefirst() || ""} ${claimCase?.getClientnamelast() || ""}`,
												isurl: false
											},
											{
												title: "Group Name",
												value: `${claimCase?.getClientgroupid() || 0}`,
												isurl: false
											}
										]}
										style={{ width: "100%" }}
										leftLabelMinWidth={120}
										rightLabelMinWidth={110}
									/>
								</Skeleton>
							</Box>
							<SectionHeader title="Claims" width="100%" />
							<Skeleton isLoaded={!!claimCase} endColor="gray.200">
								<Table>
									<Thead>
										<Tr>
											<Th>Claim ID</Th>
											<Th>Services</Th>
											<Th>Amount</Th>
											<Th>Submitted</Th>
											<Th>Status</Th>
											<Th>&nbsp;</Th>
										</Tr>
									</Thead>
									<Tbody>
										{claimList.map((claim: CaseClaimDetail) => (
											<Tr key={claim.getId()}>
												<Td>
													<LinkableTableCell
														Contents={claim.getId()}
														To={generatePath(RouterPathMapping.Attorney_ClaimDetail, {
															id: claim.getId().toString()
														})}
													/>
												</Td>
												<Td>{renderClaimServices(claim)}</Td>
												<Td>{claim.getTotalAmount()?.getValue()}</Td>
												<Td>
													{claim.getSubmittedAt() > 0 &&
														convertUnixTimestampToLocalDate(claim.getSubmittedAt()).toLocaleDateString("en-US")}
												</Td>
												<Td>{processClaimStatusForAttorney(getClaimStatusFromBffStatus(claim.getStatus()))}</Td>
												<Td>
													{getClaimStatusFromBffStatus(claim.getStatus()) === ClaimStatus.Draft && (
														<Button
															colorScheme="orange"
															as={Link}
															to={generatePath(RouterPathMapping.Attorney_ClaimIntake, {
																id: claim.getId().toString()
															})}>
															Continue
														</Button>
													)}
												</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							</Skeleton>
						</Page>
					</Box>
				</Box>
			</HStack>
		</LayoutWrapper>
	);
};

export default AttorneyCaseDetail;
